import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import {
  FileText, Upload, Download, Trash2, File, Image, FileCode, FileArchive,
  Folder, FolderPlus, Edit2, X, Check, Home, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Observação importante:
 * - Este frontend usa as rotas:
 *   GET    /api/documentos?pasta=...
 *   POST   /api/documentos/upload  (compatível, backend também aceita /api/documentos)
 *   GET    /api/documentos/:id/download
 *   DELETE /api/documentos/:id
 *   PUT    /api/documentos/:id  { novo_nome }
 *   GET    /api/pastas?pasta_pai=...
 *   POST   /api/pastas  { nome, pasta_pai }
 *   PUT    /api/pastas/:id { novo_nome }
 *   DELETE /api/pastas/:id
 */

const Documentos = ({ token, user, onLogout }) => {
  const [documentos, setDocumentos] = useState([]);
  const [pastas, setPastas] = useState([]);
  const [pastaAtual, setPastaAtual] = useState('/');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [showNovaPastaModal, setShowNovaPastaModal] = useState(false);
  const [nomePasta, setNomePasta] = useState('');

  const [editandoDocId, setEditandoDocId] = useState(null);
  const [novoNomeDoc, setNovoNomeDoc] = useState('');

  const [editandoPastaId, setEditandoPastaId] = useState(null);
  const [novoNomePasta, setNovoNomePasta] = useState('');

  useEffect(() => {
    carregarDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastaAtual]);

  const carregarDados = async () => {
    await Promise.all([carregarDocumentos(), carregarPastas()]);
  };

  const carregarDocumentos = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/documentos?pasta=${encodeURIComponent(pastaAtual)}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDocumentos(Array.isArray(data) ? data : []);
      } else {
        toast.error('Erro ao carregar documentos');
      }
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const carregarPastas = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/pastas?pasta_pai=${encodeURIComponent(pastaAtual)}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPastas(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Erro ao carregar pastas:', error);
    }
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setUploading(true);

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('pasta', pastaAtual); // ✅ necessário para o backend

        // ✅ Backend agora aceita /documentos/upload e /documentos
        const response = await fetch(`${BACKEND_URL}/api/documentos/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });

        if (!response.ok) {
          const txt = await response.text().catch(() => '');
          throw new Error(`Erro ao enviar ${file.name}: ${txt}`);
        }
      }

      toast.success(`${files.length} arquivo(s) enviado(s) com sucesso!`);
      carregarDocumentos();
      carregarPastas();
    } catch (error) {
      console.error('Erro no upload:', error);
      toast.error('Erro ao enviar arquivo(s)');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDownload = async (documento) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/documentos/${documento.id}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // ✅ Preferir nome_original, fallback nome
        const filename = documento.nome_original || documento.nome || 'arquivo';
        link.setAttribute('download', filename);

        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Download iniciado!');
      } else {
        toast.error('Erro ao baixar arquivo');
      }
    } catch (error) {
      console.error('Erro no download:', error);
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleDelete = async (documento) => {
    if (!window.confirm(`Tem certeza que deseja deletar "${documento.nome_original}"?`)) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/documentos/${documento.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Documento deletado com sucesso!');
        carregarDocumentos();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Erro ao deletar documento');
      }
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast.error('Erro ao deletar documento');
    }
  };

  const handleCriarPasta = async () => {
    if (!nomePasta.trim()) {
      toast.error('Digite um nome para a pasta');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/pastas`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nome: nomePasta.trim(),
          pasta_pai: pastaAtual
        })
      });

      if (response.ok) {
        toast.success('Pasta criada com sucesso!');
        setNomePasta('');
        setShowNovaPastaModal(false);
        carregarPastas();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Erro ao criar pasta');
      }
    } catch (error) {
      console.error('Erro ao criar pasta:', error);
      toast.error('Erro ao criar pasta');
    }
  };

  const handleDeletarPasta = async (pasta) => {
    if (!window.confirm(`Tem certeza que deseja deletar a pasta "${pasta.nome}"?`)) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/pastas/${pasta.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Pasta deletada com sucesso!');
        carregarPastas();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Erro ao deletar pasta');
      }
    } catch (error) {
      console.error('Erro ao deletar pasta:', error);
      toast.error('Erro ao deletar pasta');
    }
  };

  const handleRenomear = async (docId) => {
    if (!novoNomeDoc.trim()) {
      toast.error('Digite um novo nome');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/documentos/${docId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ novo_nome: novoNomeDoc.trim() })
      });

      if (response.ok) {
        toast.success('Documento renomeado com sucesso!');
        setEditandoDocId(null);
        setNovoNomeDoc('');
        carregarDocumentos();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Erro ao renomear');
      }
    } catch (error) {
      console.error('Erro ao renomear:', error);
      toast.error('Erro ao renomear documento');
    }
  };

  const iniciarEdicao = (doc) => {
    setEditandoDocId(doc.id);
    setNovoNomeDoc(doc.nome_original || doc.nome || '');
  };

  const cancelarEdicao = () => {
    setEditandoDocId(null);
    setNovoNomeDoc('');
  };

  const handleRenomearPasta = async (pastaId) => {
    if (!novoNomePasta.trim()) {
      toast.error('Digite um novo nome');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/pastas/${pastaId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ novo_nome: novoNomePasta.trim() })
      });

      if (response.ok) {
        toast.success('Pasta renomeada com sucesso!');
        setEditandoPastaId(null);
        setNovoNomePasta('');
        carregarPastas();
        carregarDocumentos();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Erro ao renomear pasta');
      }
    } catch (error) {
      console.error('Erro ao renomear pasta:', error);
      toast.error('Erro ao renomear pasta');
    }
  };

  const iniciarEdicaoPasta = (pasta) => {
    setEditandoPastaId(pasta.id);
    setNovoNomePasta(pasta.nome || '');
  };

  const cancelarEdicaoPasta = () => {
    setEditandoPastaId(null);
    setNovoNomePasta('');
  };

  const navegarPasta = (caminho) => setPastaAtual(caminho);

  const getBreadcrumbs = () => {
    if (pastaAtual === '/') return [];
    const parts = pastaAtual.split('/').filter(Boolean);
    const breadcrumbs = [];
    let path = '';
    parts.forEach((part) => {
      path += '/' + part;
      breadcrumbs.push({ nome: part, caminho: path });
    });
    return breadcrumbs;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ✅ converte MIME => "pdf" etc. quando necessário
  const inferExt = (doc) => {
    const nome = (doc?.nome_original || doc?.nome || '').toLowerCase();
    if (nome.includes('.') && !nome.endsWith('.')) return nome.split('.').pop();
    const tipo = (doc?.tipo || '').toLowerCase();
    if (tipo.includes('/')) {
      if (tipo === 'application/pdf') return 'pdf';
      if (tipo.startsWith('image/')) return 'img';
      if (tipo.includes('spreadsheet') || tipo.includes('excel')) return 'xlsx';
      if (tipo.includes('word')) return 'docx';
      if (tipo.includes('zip')) return 'zip';
    }
    return tipo || 'file';
  };

  const getFileIcon = (doc) => {
    const t = inferExt(doc);

    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'img'].includes(t)) {
      return <Image className="w-8 h-8 text-blue-500" />;
    }
    if (['pdf'].includes(t)) {
      return <FileText className="w-8 h-8 text-red-500" />;
    }
    if (['doc', 'docx', 'txt', 'odt'].includes(t)) {
      return <FileText className="w-8 h-8 text-blue-600" />;
    }
    if (['xls', 'xlsx', 'csv'].includes(t)) {
      return <FileCode className="w-8 h-8 text-green-600" />;
    }
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(t)) {
      return <FileArchive className="w-8 h-8 text-orange-500" />;
    }
    return <File className="w-8 h-8 text-gray-500" />;
  };

  const getTipoLabel = (doc) => {
    const ext = inferExt(doc);
    // se doc.tipo for MIME, mostra ext; se já for algo simples, mostra isso
    return ext;
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#F3F4F6' }}>
      <Sidebar currentPage="documentos" user={user} onLogout={onLogout} />

      <div style={{ flex: 1, padding: '30px' }}>
        {/* Header */}
        <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1F2937', marginBottom: '8px' }}>
              Documentos
            </h1>
            <p style={{ color: '#6B7280', fontSize: '14px' }}>
              Gerencie todos os documentos do sistema
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              onClick={() => setShowNovaPastaModal(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              <FolderPlus className="w-4 h-4 mr-2" />
              Nova Pasta
            </Button>

            <div>
              <input
                type="file"
                multiple
                onChange={handleUpload}
                style={{ display: 'none' }}
                disabled={uploading}
                id="file-upload-input"
              />
              <label
                htmlFor="file-upload-input"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: uploading ? '#93c5fd' : '#2563eb',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  border: 'none',
                  transition: 'all 0.2s',
                  pointerEvents: uploading ? 'none' : 'auto'
                }}
                onMouseEnter={(e) => {
                  if (!uploading) e.currentTarget.style.backgroundColor = '#1d4ed8';
                }}
                onMouseLeave={(e) => {
                  if (!uploading) e.currentTarget.style.backgroundColor = '#2563eb';
                }}
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Enviando...' : 'Upload Arquivo'}
              </label>
            </div>
          </div>
        </div>

        {/* Breadcrumbs */}
        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Home
            className="w-4 h-4 text-blue-600 cursor-pointer hover:text-blue-800"
            onClick={() => navegarPasta('/')}
          />
          {getBreadcrumbs().map((crumb, index) => (
            <React.Fragment key={index}>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span
                onClick={() => navegarPasta(crumb.caminho)}
                style={{
                  color: '#2563eb',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {crumb.nome}
              </span>
            </React.Fragment>
          ))}
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          <Card style={{ padding: '20px', backgroundColor: 'white' }}>
            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
              Pastas
            </div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981' }}>
              {pastas.length}
            </div>
          </Card>

          <Card style={{ padding: '20px', backgroundColor: 'white' }}>
            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
              Documentos
            </div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3B82F6' }}>
              {documentos.length}
            </div>
          </Card>
        </div>

        {/* Conteúdo */}
        <Card style={{ padding: '24px', backgroundColor: 'white' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' }}>
            Conteúdo
          </h2>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              Carregando...
            </div>
          ) : (
            <div>
              {/* Pastas */}
              {pastas.length > 0 && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '12px' }}>
                    Pastas
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                    {pastas.map((pasta) => (
                      <div
                        key={pasta.id}
                        style={{
                          padding: '16px',
                          border: '2px solid #E5E7EB',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: '#F9FAFB'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#10b981';
                          e.currentTarget.style.backgroundColor = '#ecfdf5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#E5E7EB';
                          e.currentTarget.style.backgroundColor = '#F9FAFB';
                        }}
                      >
                        {editandoPastaId === pasta.id ? (
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <Folder className="w-6 h-6 text-yellow-500" />
                            <input
                              type="text"
                              value={novoNomePasta}
                              onChange={(e) => setNovoNomePasta(e.target.value)}
                              style={{
                                flex: 1,
                                padding: '4px 8px',
                                fontSize: '14px',
                                border: '1px solid #10b981',
                                borderRadius: '4px',
                                outline: 'none'
                              }}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleRenomearPasta(pasta.id);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleRenomearPasta(pasta.id); }}
                              size="sm"
                              variant="ghost"
                              style={{ padding: '4px' }}
                            >
                              <Check className="w-4 h-4 text-green-600" />
                            </Button>
                            <Button
                              onClick={(e) => { e.stopPropagation(); cancelarEdicaoPasta(); }}
                              size="sm"
                              variant="ghost"
                              style={{ padding: '4px' }}
                            >
                              <X className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div
                              style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}
                              onClick={() => navegarPasta(pasta.caminho)}
                            >
                              <Folder className="w-6 h-6 text-yellow-500" />
                              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>
                                {pasta.nome}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <Button
                                onClick={(e) => { e.stopPropagation(); iniciarEdicaoPasta(pasta); }}
                                variant="ghost"
                                size="sm"
                                style={{ padding: '4px' }}
                              >
                                <Edit2 className="w-4 h-4 text-gray-600" />
                              </Button>
                              <Button
                                onClick={(e) => { e.stopPropagation(); handleDeletarPasta(pasta); }}
                                variant="ghost"
                                size="sm"
                                style={{ padding: '4px' }}
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documentos */}
              {documentos.length === 0 && pastas.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px' }}>
                  <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p style={{ color: '#6B7280', fontSize: '16px' }}>
                    Nenhum conteúdo nesta pasta
                  </p>
                  <p style={{ color: '#9CA3AF', fontSize: '14px', marginTop: '8px' }}>
                    Crie uma pasta ou faça upload de arquivos
                  </p>
                </div>
              ) : documentos.length > 0 ? (
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '12px' }}>
                    Arquivos
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                    {documentos.map((doc) => (
                      <Card key={doc.id} style={{
                        padding: '16px',
                        backgroundColor: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        transition: 'all 0.2s'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                          <div style={{ flexShrink: 0 }}>
                            {getFileIcon(doc)}
                          </div>

                          <div style={{ flex: 1, minWidth: 0 }}>
                            {editandoDocId === doc.id ? (
                              <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                                <input
                                  type="text"
                                  value={novoNomeDoc}
                                  onChange={(e) => setNovoNomeDoc(e.target.value)}
                                  style={{
                                    flex: 1,
                                    padding: '4px 8px',
                                    fontSize: '14px',
                                    border: '1px solid #3B82F6',
                                    borderRadius: '4px',
                                    outline: 'none'
                                  }}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleRenomear(doc.id);
                                  }}
                                />
                                <Button onClick={() => handleRenomear(doc.id)} size="sm" variant="ghost" style={{ padding: '4px' }}>
                                  <Check className="w-4 h-4 text-green-600" />
                                </Button>
                                <Button onClick={cancelarEdicao} size="sm" variant="ghost" style={{ padding: '4px' }}>
                                  <X className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                            ) : (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '4px'
                              }}>
                                <div style={{
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  color: '#1F2937',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: 1
                                }}>
                                  {doc.nome_original || doc.nome}
                                </div>
                                <Button
                                  onClick={() => iniciarEdicao(doc)}
                                  size="sm"
                                  variant="ghost"
                                  style={{ padding: '4px', flexShrink: 0 }}
                                >
                                  <Edit2 className="w-3 h-3 text-gray-600" />
                                </Button>
                              </div>
                            )}

                            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>
                              {formatFileSize(doc.tamanho)} • {getTipoLabel(doc)}
                            </div>

                            <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '12px' }}>
                              Por {doc.uploaded_by_nome} • {formatDate(doc.created_at)}
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                              <Button
                                onClick={() => handleDownload(doc)}
                                variant="outline"
                                size="sm"
                                className="flex-1"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Baixar
                              </Button>

                              <Button
                                onClick={() => handleDelete(doc)}
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </Card>
      </div>

      {/* Modal Nova Pasta */}
      {showNovaPastaModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <Card style={{
            padding: '24px',
            backgroundColor: 'white',
            width: '90%',
            maxWidth: '400px'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
              Nova Pasta
            </h3>

            <input
              type="text"
              value={nomePasta}
              onChange={(e) => setNomePasta(e.target.value)}
              placeholder="Nome da pasta"
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '14px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                marginBottom: '16px',
                outline: 'none'
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCriarPasta();
              }}
              autoFocus
            />

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setShowNovaPastaModal(false);
                  setNomePasta('');
                }}
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCriarPasta}
                className="bg-green-600 hover:bg-green-700"
              >
                Criar
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Documentos;
